import React, { HTMLAttributes, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { format } from 'modules/helpers/strings';
import { IResponsiveImageModel, IResponsiveImageSource } from 'shared/interfaces/responsiveImage';
import { constants } from 'variables';

interface IBackgroundImage extends HTMLAttributes<HTMLDivElement> {
    image: IResponsiveImageModel;
    lazyLoad: boolean;
}

function responsiveBackgroundImage(sources: IResponsiveImageSource[]): string {
    return sources
        .map((source): string => {
            const {
                breakPoint: { minWidth, maxWidth },
                images: imageValues
            } = source;

            const images = [...imageValues];
            const urlsArray = [];
            images
                .sort((a, b): number => a.width - b.width)
                .forEach((image, index): void => {
                    urlsArray.push(`url("${image.url}") ${index + 1}x`);
                });
            const urls = urlsArray.join(', ');
            let mediaQuery = '';

            if (!!minWidth && !maxWidth) {
                mediaQuery = format('min-width: {0}px')(minWidth);
            } else if (!minWidth && !!maxWidth) {
                mediaQuery = format('max-width: {0}px')(maxWidth);
            } else if (!!minWidth && !!maxWidth) {
                mediaQuery = format('min-width: {0}px) and (max-width: {1}px')(minWidth, maxWidth);
            }

            return `@media (${mediaQuery}) {
                        background-image: -webkit-image-set("${urls}");
                        background-image: image-set("${urls}");
                    }`;
        })
        .join('\n');
}

const BackgroundElement = styled.div<{ sources: IResponsiveImageSource[]; fallBackUrl: string; loadImage: boolean }>`
    flex: 1 1 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    opacity: 1;
    transition: opacity 1s ${constants.fadeTransition};

    ${({ loadImage, fallBackUrl }): string => loadImage && fallBackUrl && `background-image: url("${fallBackUrl}");`}
    ${({ loadImage, sources }): string => loadImage && sources && responsiveBackgroundImage(sources)}
    ${({ loadImage }): string => !loadImage && 'opacity: 0;'}
`;

export default function BackgroundImage({ image, lazyLoad, children, ...rest }: IBackgroundImage): JSX.Element {
    const { sources, fallBackUrl, altText } = image;
    const [lazyLoadImage, setLazyLoadImage] = useState<boolean>(lazyLoad);
    const element = useRef<HTMLDivElement>(undefined);

    useEffect((): void => {
        if (lazyLoadImage && element.current) {
            const config = {
                rootMargin: '100px 0px 500px 0px'
            };
            const intersectionObserver = new IntersectionObserver((entries): void => {
                entries.map((entry): void => {
                    if (entry.isIntersecting) {
                        requestAnimationFrame((): void => {
                            setLazyLoadImage(false);
                        });
                        intersectionObserver.unobserve(element.current);
                    }
                    return null;
                });
            }, config);

            intersectionObserver.observe(element.current);
        }
    }, []);

    return (
        <BackgroundElement
            loadImage={!lazyLoadImage}
            sources={sources}
            fallBackUrl={fallBackUrl}
            {...rest}
            ref={element}
        >
            {children}
        </BackgroundElement>
    );
}
